import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap, catchError, throwError } from 'rxjs';
import { ErrorStore } from '@shared/stores/error.store';
import { Router } from '@angular/router';
import { UserStore } from '@shared/stores/user.store';
import { OKTA_AUTH } from '@okta/okta-angular';

interface ApiResponse {
  success: boolean;
  code?: string | number;
  message?: string;
}

/**
 * Interceptor to handle HTTP errors and log them to the error store.  This only handles messages from the backend
 * that return a message that links to a translation key.  See ErrorStore for more details.
 * @param req
 * @param next
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const errorStore = inject(ErrorStore);
  const oktaAuth = inject(OKTA_AUTH);

  function logResponse(body: ApiResponse | null) {
    if (body?.success === false && body?.message)
        errorStore.logError({ success: body.success, code: body.code || 'unknown', message: body.message ?? 'Generic_RequestError' });
  }
  
  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) 
          logResponse(event.body as ApiResponse | null);
      }
    }),
    catchError((err: HttpErrorResponse) => {
      const isServerError = err.status >= 500
      // Handle 401 Unauthorized - sign out and redirect to welcome page
      if (err.status === 401) {
        oktaAuth.signOut({
          postLogoutRedirectUri: window.location.origin + '/welcome'
        }).catch(signOutError => {
          window.location.href = '/welcome';
        });
        return throwError(() => err);
      }

      if (isServerError || [403, 404].includes(err.status)) {
        errorStore.logError({
          success: false,
          code: err.status,
          message: 'Generic_ErrorMessage',
        });
      }

      if (err.status === 400 && err.error) 
        logResponse(err.error as ApiResponse | null);
      
      return throwError(() => err);
    })
  );
};
